function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Escape customer-facing text before handing it to Office as an HTML reply body. */
export function draftToReplyHtml(draftText: string): string {
  return `<pre style="font-family: Segoe UI, sans-serif; white-space: pre-wrap;">${escapeHtmlText(draftText)}</pre>`;
}

/** Draft text as HTML with explicit `<br>` line breaks, for clipboard payloads
 * pasted into rich-text editors (Outlook mobile's "Copy & Close" flow). Plain
 * `text/plain` clipboard data loses the paragraph breaks on paste, crowding
 * the whole draft into one run-on paragraph. */
function draftToClipboardHtml(draftText: string): string {
  return escapeHtmlText(draftText.replace(/\r\n?/g, '\n')).replace(/\n/g, '<br>');
}

/**
 * True when the add-in is running inside Outlook on iOS or Android.
 *
 * Outlook mobile does NOT support reply-injection from a Read-mode task pane:
 * `item.displayReplyForm` is in Microsoft's "unsupported on mobile (Message
 * Read)" list, and the compose-mode alternatives (`body.setAsync` /
 * `prependAsync`) are unsupported on mobile too. The method still *exists* on
 * the item, so a `typeof === 'function'` guard passes and the call silently
 * no-ops. Callers use this to fall back to a manual copy/paste flow instead.
 * See docs: outlook/outlook-mobile-apis (Unsupported APIs table).
 */
export function isOutlookMobile(): boolean {
  if (typeof Office === 'undefined') return false;
  const hostName = Office.context?.mailbox?.diagnostics?.hostName;
  if (hostName === 'OutlookIOS' || hostName === 'OutlookAndroid') return true;
  const platform = Office.context?.platform;
  const PlatformType = Office.PlatformType;
  return (
    platform != null &&
    PlatformType != null &&
    (platform === PlatformType.iOS || platform === PlatformType.Android)
  );
}

/**
 * Insert a reply through the real Office.js host boundary.
 * Returns false in browser preview mode, on unsupported Outlook hosts, and on
 * Outlook mobile (where reply injection is not supported — see isOutlookMobile).
 */
export function displayOutlookReply(draftText: string): boolean {
  if (typeof Office === 'undefined') return false;
  const item = Office.context?.mailbox?.item;
  if (!item || typeof item.displayReplyForm !== 'function') return false;
  // On mobile the call is a silent no-op; signal failure so the caller can
  // fall back to clipboard copy rather than pretending the insert worked.
  if (isOutlookMobile()) return false;
  item.displayReplyForm({ htmlBody: draftToReplyHtml(draftText) });
  return true;
}

/**
 * Close the add-in task pane (the "AIRA window").
 *
 * `Office.context.ui.closeContainer()` closes the task pane it's called from
 * (Outlook minimum requirement set Mailbox 1.5 — supported on Outlook mobile,
 * which caps at 1.5). Used by the mobile Copy & Close action after the draft is
 * copied so the user can open their reply and paste. Returns true when the call
 * was made; false on hosts that don't expose it (e.g. browser preview).
 */
export function closeOutlookTaskPane(): boolean {
  try {
    if (typeof Office !== 'undefined' && typeof Office.context?.ui?.closeContainer === 'function') {
      Office.context.ui.closeContainer();
      return true;
    }
  } catch {
    // Host doesn't support closeContainer — ignore.
  }
  return false;
}

/**
 * Synchronous clipboard copy via a temporary hidden element + `document.execCommand`.
 *
 * The Outlook add-in task pane runs in an embedded iframe whose Permissions
 * Policy typically blocks the async Clipboard API (`navigator.clipboard`), so
 * `writeText`/`write` reject with NotAllowedError there. `execCommand('copy')`
 * still works because it copies the current DOM selection during the click's
 * user gesture — no clipboard-write permission grant required. The selection
 * is placed on a `<div>` (rather than a plain `<textarea>`) so the browser
 * puts both `text/plain` and `text/html` on the clipboard, preserving line
 * breaks when the user pastes into a rich-text compose window instead of
 * collapsing the draft into one run-on paragraph. The div is intentionally
 * NOT `contenteditable` — copying a DOM selection carries its HTML flavor
 * regardless of editability, and making it editable risks the mobile
 * WebView treating it as a live text input (keyboard/focus side effects) on
 * exactly the Outlook-mobile flow this is for. Must be called synchronously
 * from the user gesture (before any `await`).
 */
function copyViaExecCommand(text: string): boolean {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
  const container = document.createElement('div');
  try {
    container.innerHTML = draftToClipboardHtml(text);
    // Keep it off-screen but still selectable (display:none can't be selected).
    container.style.position = 'fixed';
    container.style.top = '-9999px';
    container.style.opacity = '0';
    document.body.appendChild(container);
    const range = document.createRange();
    range.selectNodeContents(container);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    const copied = document.execCommand('copy');
    selection?.removeAllRanges();
    return copied;
  } catch {
    return false;
  } finally {
    container.remove();
  }
}

/**
 * Copy draft text to the clipboard. Used as the mobile fallback for Insert,
 * where Office.js cannot open a reply form. Returns true on success.
 *
 * Tries the synchronous `execCommand` path first because it works inside the
 * Outlook add-in iframe where the async Clipboard API is blocked, then falls
 * back to the async Clipboard API — `navigator.clipboard.write` with a
 * multi-type `ClipboardItem` when available (so formatting still survives),
 * then plain `writeText` — for hosts that allow it.
 */
export async function copyDraftToClipboard(draftText: string): Promise<boolean> {
  if (copyViaExecCommand(draftText)) return true;
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard?.write &&
    typeof ClipboardItem !== 'undefined'
  ) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([draftText], { type: 'text/plain' }),
          'text/html': new Blob([draftToClipboardHtml(draftText)], { type: 'text/html' }),
        }),
      ]);
      return true;
    } catch {
      // Some hosts gate multi-type `write()` more strictly than `writeText` —
      // fall through to the plain-text path below instead of giving up here.
    }
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(draftText);
      return true;
    }
  } catch {
    // fall through to the failure return
  }
  return false;
}
